import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ConfirmModal from "../ConfirmModal";
import { closeSpecialRouteDay, getSpecialRoutesPending } from "../../services";
import {
  SpecialOrderPendingPickup,
  SpecialRouteCloseDayResult,
  SpecialRoutePending,
} from "../../types/special-order.type";
import { Branch } from "../../types/user.type";
import { formatNumber } from "../../utils";
import {
  API_DATE_FORMAT,
  formatOrderDate,
  pendingPickupOrders,
  specialOrderDetailPath,
} from "./data";

interface CloseDayPanelProps {
  canSelectBranch: boolean;
  branches: Branch[];
}

const COLUMN_COUNT = 5;

/**
 * ปิดวันสายพิเศษ — route 99 has no driver to press ปิดงาน, so the back office closes its day
 * here once every customer has collected. The server refuses (409) while any order for the
 * route-day is still `ordered` and names them; those are listed so each can be delivered or
 * cancelled. The sales report is then created in the usual places.
 */
export default function CloseDayPanel({ canSelectBranch, branches }: CloseDayPanelProps) {
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [companyId, setCompanyId] = useState<string>("");
  const [rows, setRows] = useState<SpecialRoutePending[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmRow, setConfirmRow] = useState<SpecialRoutePending | null>(null);
  const [closing, setClosing] = useState<boolean>(false);
  const [result, setResult] = useState<
    (SpecialRouteCloseDayResult & { route_code: string }) | null
  >(null);
  const [pending, setPending] = useState<SpecialOrderPendingPickup[] | null>(null);

  const apiDate = date?.isValid() ? date.format(API_DATE_FORMAT) : "";
  const needsBranch = canSelectBranch && !companyId;

  const fetchPending = useCallback(async () => {
    if (!apiDate || needsBranch) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getSpecialRoutesPending(apiDate, canSelectBranch ? companyId : undefined);
      setRows(data ?? []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiDate, needsBranch, canSelectBranch, companyId]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleClose = async () => {
    if (!confirmRow) return;
    setClosing(true);
    try {
      const {
        data: { data },
      } = await closeSpecialRouteDay({ route_uuid: confirmRow.route_uuid, date: confirmRow.date });
      if (data) setResult({ ...data, route_code: confirmRow.route_code });
      setConfirmRow(null);
      fetchPending();
    } catch (error) {
      setConfirmRow(null);
      const orders = pendingPickupOrders(error);
      if (orders.length > 0) setPending(orders);
    } finally {
      setClosing(false);
    }
  };

  return (
    <Paper>
      <Typography variant="subtitle1" fontWeight={700}>
        ปิดวันสายพิเศษ
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        ปิดงานของสายพิเศษ (สาย 99) หลังลูกค้ามารับของครบทุกออเดอร์ของวันนั้น
        จากนั้นสร้างรายงานการขายได้ตามขั้นตอนเดิม
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          {canSelectBranch && (
            <TextField
              select
              size="small"
              label="สาขา"
              value={companyId}
              onChange={(event) => setCompanyId(event.target.value)}
              sx={{ minWidth: 260 }}
            >
              <MenuItem value="">เลือกสาขา</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.uuid} value={String(branch.company_id)}>
                  {branch.branch_code} - {branch.branch_name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <DatePicker
            label="วันที่"
            value={date}
            format="DD/MM/YYYY"
            onChange={(value) => setDate(value)}
            slotProps={{ textField: { size: "small", sx: { width: 200 } } }}
          />
        </Box>
      </LocalizationProvider>

      {result && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>
          ปิดวันสาย {result.route_code} แล้ว · {result.bill_count} บิล · ยอดรวม{" "}
          {formatNumber(result.grand_total)} บาท
          {result.sale_deposit_uuid ? " · สร้างรายการแจ้งยอดโอนของบิลเงินสดแล้ว" : ""}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
            <TableRow>
              <TableCell>สายพิเศษ</TableCell>
              <TableCell>วันที่</TableCell>
              <TableCell align="right">รอลูกค้ารับ</TableCell>
              <TableCell align="right">รับแล้ว</TableCell>
              <TableCell align="center">ปิดวัน</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {needsBranch && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">เลือกสาขาก่อน</Typography>
                </TableCell>
              </TableRow>
            )}
            {!needsBranch &&
              loading &&
              Array.from({ length: 2 }).map((_, row) => (
                <TableRow key={`skeleton-${row}`}>
                  {Array.from({ length: COLUMN_COUNT }).map((__, cell) => (
                    <TableCell key={cell}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!needsBranch && !loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    ไม่มีออเดอร์พิเศษแบบลูกค้ามารับในวันนี้
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!needsBranch &&
              !loading &&
              rows.map((row) => (
                <TableRow key={`${row.route_uuid}-${row.date}`}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.route_code}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatOrderDate(row.date)}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={row.ordered_count > 0 ? 700 : 400}
                      color={row.ordered_count > 0 ? "warning.main" : "text.primary"}
                    >
                      {row.ordered_count}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.delivered_count}</TableCell>
                  <TableCell align="center">
                    {row.closed ? (
                      <Chip size="small" color="success" label="ปิดวันแล้ว" />
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setConfirmRow(row)}
                      >
                        ปิดวันสายพิเศษ
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmModal
        open={Boolean(confirmRow)}
        title="ปิดวันสายพิเศษ"
        description={
          confirmRow
            ? `ปิดงานสาย ${confirmRow.route_code} ของวันที่ ${formatOrderDate(confirmRow.date)} ` +
              "ระบบจะส่งปิดงาน (ของเข้าเท่ากับของออก) และสร้างรายการแจ้งยอดโอนของบิลเงินสดให้"
            : undefined
        }
        loading={closing}
        confirmLabel="ปิดวันสายพิเศษ"
        onClose={() => setConfirmRow(null)}
        onConfirm={handleClose}
      >
        {confirmRow && confirmRow.ordered_count > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            ยังมี {confirmRow.ordered_count} ออเดอร์ที่ลูกค้ายังไม่มารับ ระบบจะไม่ให้ปิดวันจนกว่าจะยืนยันส่งหรือยกเลิก
          </Alert>
        )}
      </ConfirmModal>

      <Dialog open={Boolean(pending)} onClose={() => setPending(null)} fullWidth maxWidth="xs">
        <DialogTitle>ยังปิดวันไม่ได้</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" mb={1}>
            ออเดอร์เหล่านี้ลูกค้ายังไม่มารับ ให้ยืนยันส่งหรือยกเลิกก่อนปิดวัน
          </Typography>
          <List dense disablePadding>
            {(pending ?? []).map((order) => (
              <ListItem key={order.uuid} disableGutters>
                <ListItemText
                  primary={
                    <Link component={RouterLink} to={specialOrderDetailPath(order.uuid)}>
                      {order.order_no}
                    </Link>
                  }
                  secondary={order.customer_name}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
